

import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CAMHeader from "../../../components/Header/CAMHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import { useLocation, useNavigate } from "react-router-dom";
import "./auditTrail.scss";
import { useDispatch, useSelector } from "react-redux";
import * as uop from "../../../redux/audit_config/actions";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";
import Loader from "../../../components/Loader/loader";
import ApplicationStatusChip from "../../../components/StatusChip/ApplicationStatusChip";

const StatusChip = ({ status }) => <ApplicationStatusChip status={status} />;

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  return String(value);
};

const ChangesTable = ({ selectedVersion }) => {
  return (
    <Table>
      <TableHead>
        <TableRow>
          {!(
            selectedVersion?.sheet_reorder || selectedVersion?.column_reorder
          ) && (
            <TableCell className="version-detail-cell--field">Field</TableCell>
          )}

          <TableCell>Previous Value</TableCell>

          <TableCell>Value in Version {selectedVersion.version}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {Object.entries(selectedVersion.changes || {}).map(([key, change]) => (
          <TableRow key={key}>
            {/* Field */}
            {!(
              selectedVersion?.sheet_reorder || selectedVersion?.column_reorder
            ) && (
              <TableCell className="version-detail-cell--field">
                {change?.label || key}
              </TableCell>
            )}

            {/* Previous Value */}
            <TableCell className="version-detail-cell--previous">
              {formatValue(change?.old_value)}
            </TableCell>

            {/* New Value */}
            <TableCell className="version-detail-cell--new">
              {formatValue(change?.new_value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const NoChangesTable = ({ selectedVersion }) => {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>

          <TableCell>Value in Version {selectedVersion.version}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {Object.entries(selectedVersion.data || {}).map(([key, change]) => (
          <TableRow key={key}>
            <TableCell className="version-detail-cell--field">
              {change?.label || key}
            </TableCell>

            <TableCell className="version-detail-cell--previous">
              {formatValue(change?.value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const VersionHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { entityType, entityTypeLabel, entityName, entityId, auditId } =
    location.state || {};

  const versions = useSelector((state) => state?.auditConfig?.configVersions);

  const apiStatus = useSelector((state) => state?.auditConfig?.configVersions);

  // Store the complete selected version object
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [highlightedAuditId, setHighlightedAuditId] = useState(auditId);
  const [message, setMessage] = useState("");
  const [err, setErr] = useState(false);

  const versionResults = versions?.data?.results || [];

  // Clear previous version data when page loads
  useEffect(() => {
    dispatch(uop.clearConfigurationVersions());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show API error
  useEffect(() => {
    if (apiStatus?.apiError === true) {
      setMessage(apiStatus?.message || "Something went wrong");
      setErr(true);
    } else {
      setMessage("");
      setErr(false);
    }
  }, [apiStatus]);

  useEffect(() => {
    if (!entityType || !entityId) return;

    dispatch(
      uop.getConfigurationVersions({
        entity_type: entityType,
        entity_id: entityId,
      }),
    );
  }, [dispatch, entityType, entityId]);

  // Automatically select version matching auditId when API data is received
  useEffect(() => {
    if (versionResults.length > 0 && !selectedVersion) {
      const matchedVersion = versionResults.find(
        (version) => version.audit_id === auditId,
      );

      if (matchedVersion) {
        setSelectedVersion(matchedVersion);
        setHighlightedAuditId(matchedVersion.audit_id);
      } else {
        // Fallback to first version if no auditId match
        setSelectedVersion(versionResults[0]);
        setHighlightedAuditId(versionResults[0]?.audit_id);
      }
    }
  }, [versionResults, selectedVersion, auditId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleVersionSelect = (version) => {
    setSelectedVersion(version);
    setHighlightedAuditId(version.audit_id);
  };

  return (
    <>
      <CAMHeader />
      <CAMDrawer />

      <InvestigatorContainer>
        {/* Breadcrumb */}
        <Box className="version-history-breadcrumb">
          <Box className="version-history-back" onClick={handleBack}>
            <ArrowBackIcon fontSize="small" />
            <span>Back</span>
          </Box>

          <span className="breadcrumb-sep">/</span>

          <span className="breadcrumb-item">{entityName}</span>

          <ChevronRightIcon fontSize="small" className="breadcrumb-chevron" />

          <span className="breadcrumb-item breadcrumb-current">
            Version History
          </span>
        </Box>

        {/* Page title */}
        <Box className="version-history-title">{entityName}</Box>

        {/* ================= LOADING ================= */}
        {versions?.loading && <Loader />}

        {/* ================= VERSION HISTORY ================= */}
        {!versions?.loading &&
          versions?.apiSuccess &&
          versionResults.length > 0 && (
            <Box className="version-history-layout">
              {/* ================= LEFT: VERSION LIST ================= */}
              <Box className="version-history-list-panel">
                <Box className="version-history-list-header">
                  Version History
                </Box>

                <Box className="version-history-list">
                  {versionResults.map((version, index) => {
                    // Compare by audit_id to properly identify selected version
                    const isSelected = version.audit_id === highlightedAuditId;

                    return (
                      <Box
                        key={index}
                        className={`version-history-item ${
                          isSelected ? "version-history-item--active" : ""
                        }`}
                        onClick={() => handleVersionSelect(version)}
                      >
                        <Box className="version-history-item-dot" />

                        <Box className="version-history-item-body">
                          <Box className="version-history-item-top">
                            <span className="version-history-item-label">
                              v{version.version}
                            </span>

                            <StatusChip status={version.event_label} />
                          </Box>

                          <Box className="version-history-item-meta">
                            {version.created_at} · {version.created_by}
                          </Box>

                          {!(
                            version?.sheet_reorder || version?.column_reorder
                          ) &&
                            version.changes && (
                              <Box className="version-history-item-fields">
                                {Object.values(version.changes)
                                  .filter((item) => item?.label)
                                  .map((item) => item.label)
                                  .join(", ")}
                              </Box>
                            )}
                        </Box>

                        <ChevronRightIcon
                          fontSize="small"
                          className="version-history-item-chevron"
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* ================= RIGHT: VERSION DETAILS ================= */}
              <Box className="version-history-detail-panel">
                {selectedVersion ? (
                  <>
                    {/* Version Header */}
                    <Box className="version-summary-card">
                      {/* Left: Version + Date */}
                      <Box className="version-summary-info">
                        <Box className="version-summary-title">
                          Version {selectedVersion.version}
                        </Box>

                        <Box className="version-summary-date">
                          {selectedVersion.created_at}
                        </Box>
                      </Box>

                      {/* Middle: Event / Entity */}
                      <Box className="version-summary-event">
                        <Box className="version-summary-event-label">
                          {selectedVersion?.event_label}
                        </Box>

                        <Box className="version-summary-entity">
                          {entityName}
                        </Box>
                      </Box>

                      {/* Right: Status */}
                      <Box>
                        <StatusChip status={selectedVersion.status_label} />
                      </Box>

                      {/* Updated By */}
                      <Box className="version-summary-updated">
                        <span className="version-summary-updated-label">
                          Updated By
                        </span>

                        <span className="version-summary-updated-value">
                          {selectedVersion.created_by}
                        </span>
                      </Box>
                    </Box>

                    {/* Deleted / Restored */}
                    {selectedVersion?.is_deleted === true ? (
                      <Box
                        component="section"
                        sx={{
                          p: 1,
                          m: 1,
                          border: "1px solid #C4321F",
                          backgroundColor: "#FDEBEC",
                          borderRadius: "4px",
                        }}
                      >
                        {/* This Record has been
                        {selectedVersion?.is_deleted ? " deleted" : " approved"}
                        . No changes are available to view. */}
                        No records are available to view
                      </Box>
                    ) : selectedVersion?.show_changes_table === true ? (
                      <>
                        {/* Changes Heading */}
                        <Box className="version-detail-changes-header">
                          Changes in this version
                        </Box>

                        {selectedVersion?.column_reorder && (
                          <Box className="key-value-row">
                            <span className="key">Sheet Name:</span>
                            <span className="value">
                              {selectedVersion?.metadata?.sheet_name || "-"}
                            </span>
                          </Box>
                        )}

                        {/* Changes Table */}
                        <TableContainer
                          component={Paper}
                          className="version-detail-table"
                          elevation={0}
                        >
                          <ChangesTable selectedVersion={selectedVersion} />
                        </TableContainer>
                      </>
                    ) : (
                      <>
                        <Box className="version-detail-changes-header">
                          Values in this version
                        </Box>
                        <TableContainer
                          component={Paper}
                          className="version-detail-table"
                          elevation={0}
                        >
                          <NoChangesTable selectedVersion={selectedVersion} />
                        </TableContainer>
                      </>
                    )}
                  </>
                ) : (
                  <Box className="version-detail-empty">
                    Select a version to view changes
                  </Box>
                )}
              </Box>
            </Box>
          )}

        {/* ================= EMPTY STATE ================= */}
        {!versions?.loading &&
          versions?.apiSuccess &&
          versionResults.length === 0 && (
            <Box className="version-detail-empty" sx={{ padding: 4 }}>
              No version history data available.
            </Box>
          )}

        {/* ================= ERROR ================= */}
        {err && (
          <CustomizedSnackbars
            severity="error"
            state={err}
            text={message}
            handleClose={() => setErr(false)}
          />
        )}
      </InvestigatorContainer>
    </>
  );
};

export default VersionHistory;
